import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  LinearProgress,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@icons/ExpandMoreIcon";
import { getSelectedCountryCode } from "@utils/countryUtils";
import {
  FacilityCompliancePayload,
} from "@services/facilityCompliance";
import { isTaxComplianceCalendarItem } from "@utils/penaltyCalendarTax";
import useStreamingCompliance from "@hooks/useStreamingCompliance";
import {
  formatDate,
  formatLicenseFee,
} from "@utils/formatUtils";
import { getCurrentUserId } from "@utils/userUtils";
import { AuditChecklistTable } from "@components/Spaces/AuditChecklistTable";

// Styled components
const MainContainer = styled(Box)({
  display: "flex",
  width: "100%",
  height: "99vh",
  background: "#ECFCFF",
});

const ContentWrapper = styled(Box)({
  padding: "8px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "16px",
  overflowY: "auto",
  width: "100%",
});

const ContentContainer = styled(Box)({
  backgroundColor: "#fdffff",
  borderRadius: "12px",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
  position: "relative",
  overflow: "hidden",
  width: "100%",
  maxWidth: "1000px",
  padding: "16px",
});

const StyledAccordion = styled(Accordion)({
  marginBottom: "8px",
  borderRadius: "8px !important",
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  "&:before": {
    display: "none",
  },
});

const AccordionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  color: "#262626",
});

const ProgressContainer = styled(Box)({
  width: "100%",
  maxWidth: "1000px",
  marginBottom: "16px",
});

const SectionSkeleton = styled(Box)({
  marginBottom: "8px",
  borderRadius: "8px",
  border: "1px solid #e0e0e0",
  padding: "16px",
  background: "#f5f5f5",
});

// Section configuration with labels and icons
const SECTION_CONFIG = {
  licensingRequirements: {
    label: "Licensing Requirements",
    description: "Federal, State, County, and City licensing requirements",
    icon: "📋",
  },
  accreditationRequirements: {
    label: "Accreditation Requirements",
    description: "Conditions of participation and accrediting organizations",
    icon: "🏆",
  },
  complianceComparison: {
    label: "Compliance Comparison",
    description: "Completed, pending, and upcoming deadlines",
    icon: "📊",
  },
  penaltiesAndTotalExposure: {
    label: "Penalties & Compliance Calendar",
    description: "Penalty items, costs, and compliance calendar",
    icon: "⚠️",
  },
  auditReadyChecklist: {
    label: "Audit-Ready Checklist",
    description: "Document checklist for regulatory audits",
    icon: "✅",
  },
};

interface FacilityComplianceStreamingProps {
  facilityData?: any;
}

const FacilityComplianceStreaming: React.FC<FacilityComplianceStreamingProps> = ({
  facilityData: propFacilityData,
}) => {
  const location = useLocation();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [payload, setPayload] = useState<FacilityCompliancePayload | null>(null);

  // Get facility data from navigation state or props
  const facilityData = propFacilityData || location.state?.facilityData;

  // Helper function to get facility data with fallback to sessionStorage
  const getFacilityData = () => {
    if (facilityData) {
      return facilityData;
    }
    try {
      const stored = sessionStorage.getItem("filtered_selectedFacility");
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      console.error("Error parsing facility data from sessionStorage:", err);
      return null;
    }
  };

  // Build payload when facility data is available
  useEffect(() => {
    const currentFacilityData = getFacilityData();
    if (currentFacilityData) {
      const state = currentFacilityData.state ? String(currentFacilityData.state).trim() : "";
      const city = currentFacilityData.city ? String(currentFacilityData.city).trim() : "";
      const userId = parseInt(getCurrentUserId(), 10) || 0;
      const newPayload: FacilityCompliancePayload = {
        user_id: userId,
        organization: currentFacilityData.site_name || "Facility",
        facility_name: currentFacilityData.site_name || "Facility",
        facility_type: currentFacilityData.facility_type_slug || currentFacilityData.facility_type,
        country: getSelectedCountryCode(),
        state: state,
        county: "",
        city: city,
        site_url: "",
        documents: "",
        as_of_date: new Date().toISOString().split("T")[0],
        response_language: "English",
        instructions: "Search government regulatory sites for compliance requirements",
        output_format: "JSON",
        org_name: "test",
        facility_type_id: (currentFacilityData as { facility_type_id?: number }).facility_type_id,
      };
      const taxToggle = (currentFacilityData as { show_tax_compliance_calendar?: unknown })
        ?.show_tax_compliance_calendar;
      if (taxToggle === true || taxToggle === false) {
        newPayload.show_tax_compliance_calendar = taxToggle;
      }
      setPayload(newPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityData]);

  // Use streaming hook
  const {
    isConnected,
    isComplete,
    error,
    entityDetails,
    sections,
    completedSections,
    progress,
    connect,
  } = useStreamingCompliance(payload);

  // Auto-connect when payload is ready
  useEffect(() => {
    if (payload && !isConnected && !isComplete && completedSections.length === 0) {
      console.log("📡 Auto-connecting streaming...");
      connect();
    }
  }, [payload, isConnected, isComplete, completedSections.length, connect]);

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleRetry = () => {
    connect();
  };

  // Render a section loading skeleton
  const renderSectionSkeleton = (sectionKey: string) => {
    const config = SECTION_CONFIG[sectionKey as keyof typeof SECTION_CONFIG];
    return (
      <SectionSkeleton key={sectionKey}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <CircularProgress size={20} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {config?.icon} {config?.label || sectionKey}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        </Box>
      </SectionSkeleton>
    );
  };

  // Render licensing requirements accordion
  const renderLicensingAccordion = () => {
    const data = sections.licensingRequirements;
    if (!data) return renderSectionSkeleton("licensingRequirements");

    const allRequirements = [
      ...(data.country || []).map((r) => ({ ...r, level: "Federal" })),
      ...(data.state || []).map((r) => ({ ...r, level: "State" })),
      ...(data.county || []).map((r) => ({ ...r, level: "County" })),
      ...(data.city || []).map((r) => ({ ...r, level: "City" })),
    ];

    return (
      <StyledAccordion
        expanded={expanded === "licensing"}
        onChange={handleAccordionChange("licensing")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip label="✓" size="small" color="success" />
            <AccordionTitle>📋 Licensing Requirements</AccordionTitle>
            <Chip label={`${allRequirements.length} items`} size="small" variant="outlined" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {allRequirements.map((req, idx) => (
            <Box key={idx} sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {req.name}
                </Typography>
                <Chip label={req.level} size="small" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {req.category}
              </Typography>
              {req.fee && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Fee: {formatLicenseFee(req.fee, req).displayText}
                </Typography>
              )}
              {req.citation?.url && (
                <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                  <a href={req.citation.url} target="_blank" rel="noopener noreferrer">
                    📎 {req.citation.title}
                  </a>
                </Typography>
              )}
            </Box>
          ))}
        </AccordionDetails>
      </StyledAccordion>
    );
  };

  // Render accreditation requirements accordion
  const renderAccreditationAccordion = () => {
    const data = sections.accreditationRequirements;
    if (!data) return renderSectionSkeleton("accreditationRequirements");

    return (
      <StyledAccordion
        expanded={expanded === "accreditation"}
        onChange={handleAccordionChange("accreditation")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip label="✓" size="small" color="success" />
            <AccordionTitle>🏆 Accreditation Requirements</AccordionTitle>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {data.conditionsOfParticipation?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Conditions of Participation
              </Typography>
              {data.conditionsOfParticipation.map((cop, idx) => (
                <Chip key={idx} label={cop} size="small" sx={{ mr: 1, mb: 1 }} />
              ))}
            </Box>
          )}
          {data.accreditingOrganizations?.map((org, idx) => (
            <Box key={idx} sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {org.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {org.deemedStatusRequirements}
              </Typography>
              {org.citation?.url && (
                <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                  <a href={org.citation.url} target="_blank" rel="noopener noreferrer">
                    📎 {org.citation.title}
                  </a>
                </Typography>
              )}
            </Box>
          ))}
        </AccordionDetails>
      </StyledAccordion>
    );
  };

  // Render compliance comparison accordion
  const renderComparisonAccordion = () => {
    const data = sections.complianceComparison;
    if (!data) return renderSectionSkeleton("complianceComparison");

    return (
      <StyledAccordion
        expanded={expanded === "comparison"}
        onChange={handleAccordionChange("comparison")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip label="✓" size="small" color="success" />
            <AccordionTitle>📊 Compliance Comparison</AccordionTitle>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "success.main" }}>
                Completed ({data.completed?.length || 0})
              </Typography>
              {data.completed?.slice(0, 3).map((item, idx) => (
                <Typography key={idx} variant="body2" sx={{ mt: 1 }}>
                  ✅ {item.text}
                </Typography>
              ))}
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "warning.main" }}>
                Pending ({data.pending?.length || 0})
              </Typography>
              {data.pending?.slice(0, 3).map((item, idx) => (
                <Typography key={idx} variant="body2" sx={{ mt: 1 }}>
                  ⏳ {item.text}
                </Typography>
              ))}
            </Box>
          </Box>
          {data.deadlines?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Upcoming Deadlines
              </Typography>
              {data.deadlines.slice(0, 5).map((deadline, idx) => (
                <Box key={idx} sx={{ mt: 1, p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                  <Typography variant="body2">
                    📅 {formatDate(deadline.nextDueDate)} - {deadline.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </StyledAccordion>
    );
  };

  // Render penalties accordion
  const renderPenaltiesAccordion = () => {
    const data = sections.penaltiesAndTotalExposure;
    if (!data) return renderSectionSkeleton("penaltiesAndTotalExposure");

    return (
      <StyledAccordion
        expanded={expanded === "penalties"}
        onChange={handleAccordionChange("penalties")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip label="✓" size="small" color="success" />
            <AccordionTitle>⚠️ Penalties & Compliance Calendar</AccordionTitle>
            <Chip
              label={`${data.items?.length || 0} penalties`}
              size="small"
              variant="outlined"
              color="error"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {data.items?.slice(0, 5).map((penalty, idx) => (
            <Box key={idx} sx={{ mb: 2, p: 2, border: "1px solid #ffcdd2", borderRadius: 1, bgcolor: "#fff5f5" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {penalty.name}
                </Typography>
                <Chip label={penalty.jurisdiction} size="small" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {penalty.ruleOrQuote}
              </Typography>
            </Box>
          ))}
          {(() => {
            const cal = data.complianceCalendar ?? [];
            if (cal.length === 0) return null;
            const fd = getFacilityData();
            const showTaxCal = fd?.show_tax_compliance_calendar === true;
            const facilityCal = cal.filter(
              (item) => !isTaxComplianceCalendarItem(item),
            );
            const taxCal = cal.filter((item) => isTaxComplianceCalendarItem(item));
            const renderRows = (items: typeof cal) =>
              items.slice(0, 5).map((item, idx) => (
                <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: "#e3f2fd", borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    📅 {item.deadline} - {item.item}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Penalty if missed: {item.penaltyIfMissed}
                  </Typography>
                  {Array.isArray((item as any).somTags) && (item as any).somTags.length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Survey tags: {(item as any).somTags.join(", ")}
                    </Typography>
                  )}
                </Box>
              ));
            return (
              <Box sx={{ mt: 2 }}>
                {facilityCal.length > 0 && (
                  <Box sx={{ mb: showTaxCal && taxCal.length > 0 ? 2 : 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Facility Compliance Calendar ({facilityCal.length} items)
                    </Typography>
                    {renderRows(facilityCal)}
                  </Box>
                )}
                {showTaxCal && taxCal.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Tax Compliance Calendar ({taxCal.length} items)
                    </Typography>
                    {renderRows(taxCal)}
                  </Box>
                )}
              </Box>
            );
          })()}
        </AccordionDetails>
      </StyledAccordion>
    );
  };

  // Render audit checklist accordion
  const renderAuditAccordion = () => {
    const data = sections.auditReadyChecklist;
    if (!data) return renderSectionSkeleton("auditReadyChecklist");

    return (
      <StyledAccordion
        expanded={expanded === "audit"}
        onChange={handleAccordionChange("audit")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip label="✓" size="small" color="success" />
            <AccordionTitle>✅ Audit-Ready Checklist</AccordionTitle>
            <Chip label={`${data.length} items`} size="small" variant="outlined" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <AuditChecklistTable items={data} />
        </AccordionDetails>
      </StyledAccordion>
    );
  };

  // Loading state before payload is ready
  if (!payload) {
    return (
      <MainContainer>
        <ContentWrapper>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress />
            <Typography>Loading facility data...</Typography>
          </Box>
        </ContentWrapper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <ContentWrapper>
        {/* Progress bar */}
        <ProgressContainer>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {isComplete
                ? "✅ All sections loaded"
                : isConnected
                ? `📡 Streaming sections... (${completedSections.length}/5)`
                : "Ready to stream"}
            </Typography>
            <Typography variant="body2">{progress}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: "#e0e0e0",
              "& .MuiLinearProgress-bar": {
                bgcolor: isComplete ? "#4caf50" : "#2196f3",
              },
            }}
          />
        </ProgressContainer>

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ width: "100%", maxWidth: 1000 }}>
            {error}
            <Button size="small" onClick={handleRetry} sx={{ ml: 2 }}>
              Retry
            </Button>
          </Alert>
        )}

        {/* Entity details header */}
        {entityDetails && (
          <ContentContainer>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {entityDetails.facility_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {entityDetails.facility_type} • {entityDetails.city}, {entityDetails.state},{" "}
              {entityDetails.country}
            </Typography>
          </ContentContainer>
        )}

        {/* Accordion sections - render as they become available */}
        <ContentContainer>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Compliance Sections
          </Typography>

          {renderLicensingAccordion()}
          {renderAccreditationAccordion()}
          {renderComparisonAccordion()}
          {renderPenaltiesAccordion()}
          {renderAuditAccordion()}
        </ContentContainer>
      </ContentWrapper>
    </MainContainer>
  );
};

export default FacilityComplianceStreaming;
